import React, { useEffect, useMemo, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { Trash2, Edit, GripVertical, Download } from "lucide-react";

export default function Categories() {
  const { currentId } = useCompetition();
  const [items, setItems] = useState([]);
  const [nome, setNome] = useState("");
  const [tempo, setTempo] = useState(5);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [competitions, setCompetitions] = useState([]);
  const [importSource, setImportSource] = useState("");
  const [importOverwrite, setImportOverwrite] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!currentId) return;
    const { data } = await api.get(`/competitions/${currentId}/categories`);
    setItems(data);
  };

  useEffect(() => {
    load();
    api.get("/competitions").then((r) => setCompetitions(r.data)).catch(() => {});
     
  }, [currentId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        nome,
        tempo_minutos: parseInt(tempo || 5),
        ordem: editingId ? items.find((i) => i.id === editingId)?.ordem : (items.length + 1),
      };
      if (editingId) await api.put(`/categories/${editingId}`, payload);
      else await api.post(`/competitions/${currentId}/categories`, payload);
      setNome("");
      setTempo(5);
      setEditingId(null);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir categoria e atletas/notas relacionados?")) return;
    await api.delete(`/categories/${id}`);
    await load();
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[idx];
    const b = items[target];
    await api.put(`/categories/${a.id}`, { nome: a.nome, tempo_minutos: a.tempo_minutos, ordem: b.ordem });
    await api.put(`/categories/${b.id}`, { nome: b.nome, tempo_minutos: b.tempo_minutos, ordem: a.ordem });
    await load();
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    // ghost invisible so Chrome doesn't drag a text preview
    if (e.dataTransfer.setDragImage) {
      const img = new Image();
      e.dataTransfer.setDragImage(img, 0, 0);
    }
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== overId) setOverId(id);
  };
  const onDragEnd = () => {
    setDragId(null);
    setOverId(null);
  };
  const onDrop = (targetId) => async (e) => {
    e.preventDefault();
    const srcId = dragId;
    setDragId(null);
    setOverId(null);
    if (!srcId || srcId === targetId) return;
    const srcIdx = items.findIndex((x) => x.id === srcId);
    const dstIdx = items.findIndex((x) => x.id === targetId);
    if (srcIdx === -1 || dstIdx === -1) return;
    // Otimistic reorder
    const next = [...items];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(dstIdx, 0, moved);
    // Reatribui ordem 1..N e persiste apenas as que mudaram
    const withNewOrder = next.map((c, i) => ({ ...c, _newOrdem: i + 1 }));
    setItems(withNewOrder.map((c) => ({ ...c, ordem: c._newOrdem })));
    setSaving(true);
    try {
      const changed = withNewOrder.filter((c) => c._newOrdem !== c.ordem);
      await Promise.all(
        changed.map((c) =>
          api.put(`/categories/${c.id}`, {
            nome: c.nome,
            tempo_minutos: c.tempo_minutos,
            ordem: c._newOrdem,
          })
        )
      );
      await load();
    } finally {
      setSaving(false);
    }
  };

  const importSources = useMemo(
    () => competitions.filter((c) => c.id !== currentId),
    [competitions, currentId]
  );

  const doImport = async () => {
    if (!importSource) return;
    if (importOverwrite && !window.confirm("Substituir TODAS as categorias existentes desta competição? Atletas e notas vinculados serão removidos.")) return;
    setImporting(true);
    setImportMsg("");
    try {
      const { data } = await api.post(`/competitions/${currentId}/categories/import`, {
        source_competition_id: importSource,
        overwrite: importOverwrite,
      });
      setImportMsg(`✓ ${data.imported} categorias importadas de "${data.source}"`);
      setImportSource("");
      await load();
    } catch (e) {
      setImportMsg("Erro: " + extractErrorMessage(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Configuração</div>
          <h1 className="font-display text-5xl font-black tracking-tight">CATEGORIAS</h1>
        </div>
        <CompetitionSelector />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="card p-5">
            <div className="overline mb-3">
              {editingId ? "Editar Categoria" : "Nova Categoria"}
            </div>
            <form onSubmit={submit} className="space-y-3">
              <input
                data-testid="cat-nome"
                className="input-field"
                placeholder="Ex: BODYBUILDING ESTREANTE"
                required
                value={nome}
                onChange={(e) => setNome(e.target.value.toUpperCase())}
              />
              <div className="text-[11px] text-[var(--text-secondary)] border border-white/10 px-3 py-2" data-testid="cat-tempo-hint">
                O tempo de palco é calculado automaticamente pelo nº de inscritos (faixas da prévia + classificados da final) — configure em <b>Competições → Tempos do roteiro</b>.
              </div>
              {error && (
                <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2">
                  {error}
                </div>
              )}
              <div className="flex gap-2">
                <button data-testid="cat-submit" className="btn-primary flex-1">
                  {editingId ? "Atualizar" : "Adicionar"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => {
                      setEditingId(null);
                      setNome("");
                      setTempo(5);
                    }}
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="card p-5 mt-4" data-testid="cat-import-box">
            <div className="overline mb-3 flex items-center gap-2">
              <Download size={13} /> Importar de outra competição
            </div>
            <p className="text-xs text-[var(--text-secondary)] mb-3">
              Copie todas as categorias de outra competição (ex: Sobral Battle
              com 71 categorias) para esta competição.
            </p>
            <select
              data-testid="cat-import-source"
              className="input-field mb-2"
              value={importSource}
              onChange={(e) => setImportSource(e.target.value)}
            >
              <option value="">Selecione a competição de origem</option>
              {importSources.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-xs mb-3 cursor-pointer">
              <input
                data-testid="cat-import-overwrite"
                type="checkbox"
                className="accent-[#C9A84C]"
                checked={importOverwrite}
                onChange={(e) => setImportOverwrite(e.target.checked)}
              />
              <span>
                Substituir categorias existentes{" "}
                <span className="text-[#C9A84C]">
                  (remove atletas e notas)
                </span>
              </span>
            </label>
            <button
              type="button"
              data-testid="cat-import-btn"
              className="btn-primary w-full"
              onClick={doImport}
              disabled={!importSource || importing || !currentId}
            >
              {importing ? "Importando..." : "IMPORTAR CATEGORIAS"}
            </button>
            {importMsg && (
              <div
                data-testid="cat-import-msg"
                className={
                  "mt-3 text-xs px-3 py-2 border " +
                  (importMsg.startsWith("Erro")
                    ? "text-[#C9A84C] border-[#C9A84C]/30 bg-[#C9A84C]/10"
                    : "text-[#5AF78E] border-[#5AF78E]/30 bg-[#5AF78E]/10")
                }
              >
                {importMsg}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="card">
            <div className="px-4 py-2 text-[11px] text-[var(--text-secondary)] border-b border-white/5 flex items-center justify-between">
              <span>Arraste pelo <GripVertical size={11} className="inline align-middle" /> para reordenar. O Maestro, Roteiro e Backstage seguem esta ordem.</span>
              {saving && <span className="text-[#C9A84C]">Salvando…</span>}
            </div>
            <table className="table-dense">
              <thead>
                <tr>
                  <th className="w-8"></th>
                  <th className="w-14">#</th>
                  <th>Categoria</th>
                  <th className="text-right pr-4">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c, idx) => (
                  <tr
                    key={c.id}
                    data-testid={`cat-row-${c.id}`}
                    draggable
                    onDragStart={onDragStart(c.id)}
                    onDragOver={onDragOver(c.id)}
                    onDrop={onDrop(c.id)}
                    onDragEnd={onDragEnd}
                    className={
                      "transition-colors " +
                      (dragId === c.id ? "opacity-40 " : "") +
                      (overId === c.id && dragId !== c.id
                        ? "bg-[#C9A84C]/10 outline outline-1 outline-[#C9A84C]/40"
                        : "")
                    }
                  >
                    <td
                      className="cursor-grab active:cursor-grabbing text-[var(--text-secondary)] hover:text-white"
                      data-testid={`cat-drag-${c.id}`}
                      title="Arraste para reordenar"
                    >
                      <GripVertical size={14} />
                    </td>
                    <td className="tabular-nums font-display font-bold text-lg">{idx + 1}</td>
                    <td>{c.nome}</td>
                    <td className="text-right pr-4">
                      <button
                        className="btn-ghost mr-1"
                        data-testid={`cat-edit-${c.id}`}
                        onClick={() => {
                          setEditingId(c.id);
                          setNome(c.nome);
                          setTempo(c.tempo_minutos);
                        }}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className="btn-ghost"
                        data-testid={`cat-del-${c.id}`}
                        onClick={() => del(c.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-[var(--text-secondary)]">
                      Nenhuma categoria. Adicione a primeira.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
